import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, tap, throwError } from 'rxjs';
import { BrowserStorageService } from './browser-storage.service';
import { DeviceIdService } from './device-id.service';
import { noCheckToken, refreshTokenContext } from './auth-interceptor.service';
import { LoginResponseDTO } from '../interfaces/login.response.dto';
import { UserDTO } from '../interfaces/userinfo.response.dto';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  baseUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private browserStorageService: BrowserStorageService,
    private deviceIdService: DeviceIdService,
  ) {}
  loginWithToken(): Observable<LoginResponseDTO> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post<LoginResponseDTO>(this.baseUrl + '/auth/auto-login', {
      headers: headers,
      context: noCheckToken(),
    });
  }

  userInfo(): Observable<UserDTO> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<UserDTO>(this.baseUrl + '/auth/me', {
      headers: headers,
    });
  }

  refreshToken(): Observable<any> {
    // Intenta obtener el refresh token de localStorage
    let refreshToken = localStorage.getItem('refresh_token');
    // Si no está en localStorage, intenta obtenerlo de la cookie
    if (!refreshToken) {
      refreshToken = this.getCookie('refresh_token');
    }
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token found'));
    }
    // Envía el refresh token como query param (para compatibilidad con el endpoint)
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post<any>(
      `${this.baseUrl}/auth/refresh?refreshToken=${refreshToken}`,
      {}, // No enviar body, solo options
      {
        headers: headers,
        context: noCheckToken(),
      },
    );
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)'),
    );
    return match ? match[2] : null;
  }

  /**
   * Expira la cookie sin saber con qué `domain` se seteó: prueba host-only y
   * cada sufijo del hostname. Los dominios inválidos los ignora el browser.
   */
  private deleteCookie(name: string): void {
    const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = expired;
    const labels = window.location.hostname.split('.');
    for (let i = 0; i < labels.length - 1; i++) {
      const domain = labels.slice(i).join('.');
      document.cookie = `${expired}; domain=${domain}`;
      document.cookie = `${expired}; domain=.${domain}`;
    }
  }

  logout(): Observable<any> {
    this.browserStorageService.removeItem('token');
    this.browserStorageService.removeItem('user');
    this.browserStorageService.removeItem('organizationId');
    // The trusted-device id survives the logout on purpose: clearing it would make the
    // next login register a new device and ask for 2FA again.
    this.deviceIdService.clearStorageKeepingDeviceId();
    sessionStorage.clear();
    // localStorage.clear() no toca cookies, y refreshToken() usa la cookie como
    // fallback: si sobrevive, el interceptor vuelve a escribir token y
    // refresh_token en localStorage y la sesión revive.
    this.deleteCookie('refresh_token');
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get(this.baseUrl + '/auth/logout', {
      headers: headers,
    });
  }
  editPassword(userId?: string, body?: any): Observable<any> {
    return this.http.put<any>(
      this.baseUrl + '/users/' + userId + '/create-password',
      body,
    );
  }
  recoverPassword(email: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/auth/send-password-recovery-email/${email}`,
      {
        context: noCheckToken(),
      },
    );
  }
  login(body: any): Observable<HttpResponse<LoginResponseDTO>> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post<LoginResponseDTO>(
      this.baseUrl + '/auth/ext-login',
      body,
      {
        headers,
        context: noCheckToken(),
        withCredentials: true,
        observe: 'response',
      },
    );
  }
  /**
   * The response is observed whole because a successful verification with
   * `rememberDevice: true` returns the trusted-device id in the `X-Device-Id` header.
   */
  verify2FA(body: any): Observable<HttpResponse<any>> {
    return this.http.post<any>(`${this.baseUrl}/auth/verify-2fa`, body, {
      withCredentials: true,
      observe: 'response',
    });
  }
}
